import { useInfiniteQuery } from 'react-query';
import { useMemo } from 'react';
import { queryKey } from 'constants/queryKey';
import CommentsApi from 'app/modules/comments/api';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';
import { ApiClientError } from 'api/client';

type Props = {
  postId: number;
  token: string;
};

type PublicCommentsPage = Awaited<
  ReturnType<typeof CommentsApi.publicComments>
>;

export const usePublicCommentsQuery = ({ postId, token }: Props) => {
  const comments = useInfiniteQuery<PublicCommentsPage, ApiClientError>(
    queryKey.publicPostComments({ postId, token }),
    ({ pageParam = 1 }) =>
      CommentsApi.publicComments(postId, { token, page: pageParam }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.pagination.pageCount > allPages.length
          ? allPages.length + 1
          : undefined,
    },
  );

  const data = useMemo(
    () => comments.data?.pages.flatMap((page) => page.data) ?? [],
    [comments.data],
  );

  const [ref, { rootRef }] = useInfiniteScroll({
    loading: comments.isLoading || comments.isFetchingNextPage,
    hasNextPage: !!comments.hasNextPage,
    onLoadMore: () => comments.fetchNextPage(),
  });

  return {
    data,
    isLoading: comments.isLoading,
    isFetchingNextPage: comments.isFetchingNextPage,
    hasNextPage: !!comments.hasNextPage,
    ref,
    rootRef,
  };
};
